import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
} from "@mui/material";
import axios from "axios";
import { useSWRConfig } from "swr";
import CacheManagementHeader from "./cache-management-header";
import CacheActions from "./cache-actions";
import CacheSummary from "./cache-summary";
import CacheTagAccordion from "./cache-tag-accordion";
import CacheWarmingResult from "./cache-warming-result";
import { useCacheSnapshot } from "./use-cache-snapshot";
import { filterTagGroups } from "./cache-utils";
import {
  purgeAllCache,
  purgeCacheKey,
  purgeCacheTag,
  warmCache,
} from "../services/cache-service";
import {
  CacheEntryInfo,
  CacheWarmingResult as CacheWarmingResultModel,
} from "../services/cache-types";
import { useNotification } from "../../context/notification-provider";
import { useDialogs } from "../dialog/use-dialog";

// SWR key used by useCacheKeys (the per-widget refresh buttons).
const CACHE_KEYS_SWR_KEY = "/cache/keys";

const isPermissionError = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403);

const CacheManagement = () => {
  const { data, error, isLoading, refresh } = useCacheSnapshot();
  const { mutate } = useSWRConfig();
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const [searchTerm, setSearchTerm] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isWarming, setIsWarming] = useState(false);
  const [warmingResult, setWarmingResult] =
    useState<CacheWarmingResultModel | null>(null);

  const entriesByKey = useMemo(
    () =>
      new Map<string, CacheEntryInfo>(
        (data?.entries ?? []).map((entry) => [entry.key, entry]),
      ),
    [data],
  );

  const tagGroups = useMemo(
    () => filterTagGroups(data?.groupedByTag ?? [], searchTerm),
    [data, searchTerm],
  );

  const untaggedKeys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const keys = data?.untaggedKeys ?? [];
    return term ? keys.filter((key) => key.toLowerCase().includes(term)) : keys;
  }, [data, searchTerm]);

  const refreshAll = async () => {
    await Promise.all([refresh(), mutate(CACHE_KEYS_SWR_KEY)]);
  };

  const runPurge = async (
    action: () => Promise<void>,
    successMessage: string,
    failureMessage: string,
  ) => {
    setIsBusy(true);
    try {
      await action();
      showNotification({ message: successMessage, severity: "success" });
    } catch (err) {
      showNotification({
        message: `${failureMessage} ${(err as Error).message ?? ""}`.trim(),
        severity: "error",
      });
    } finally {
      await refreshAll();
      setIsBusy(false);
    }
  };

  const handleWarm = async () => {
    setIsBusy(true);
    setIsWarming(true);
    try {
      const result = await warmCache();
      setWarmingResult(result);
      showNotification({
        message: result.success
          ? "Cache warmed successfully."
          : "Cache warming completed with errors.",
        severity: result.success ? "success" : "warning",
      });
    } catch (err) {
      showNotification({
        message: `Cache warming failed. ${(err as Error).message ?? ""}`.trim(),
        severity: "error",
      });
    } finally {
      await refreshAll();
      setIsWarming(false);
      setIsBusy(false);
    }
  };

  const handlePurgeAll = async () => {
    const confirmed = await confirm(
      "This removes every entry from the cache. Data will be reloaded from the database on next use.",
      { title: "Purge entire cache?", okText: "Purge all", severity: "error" },
    );
    if (!confirmed) return;

    await runPurge(
      purgeAllCache,
      "Cache purged.",
      "Failed to purge the cache.",
    );
  };

  const handlePurgeTag = async (tag: string) => {
    const confirmed = await confirm(`Purge every key under the tag "${tag}"?`, {
      title: "Purge tag?",
      okText: "Purge tag",
      severity: "error",
    });
    if (!confirmed) return;

    await runPurge(
      () => purgeCacheTag(tag),
      `Tag "${tag}" purged.`,
      `Failed to purge tag "${tag}".`,
    );
  };

  const handlePurgeKey = async (key: string) => {
    const confirmed = await confirm(`Purge the cache key "${key}"?`, {
      title: "Purge key?",
      okText: "Purge key",
      severity: "error",
    });
    if (!confirmed) return;

    await runPurge(
      () => purgeCacheKey(key),
      `Key "${key}" purged.`,
      `Failed to purge key "${key}".`,
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          {isPermissionError(error)
            ? "You do not have permission to manage the cache."
            : "Failed to load the cache snapshot. Please try again later."}
        </Alert>
      );
    }

    const totalKeys = data?.entries.length ?? 0;
    const hasResults = tagGroups.length > 0 || untaggedKeys.length > 0;

    return (
      <>
        <CacheSummary
          totalKeys={totalKeys}
          tagCount={data?.groupedByTag.length ?? 0}
          untaggedCount={data?.untaggedKeys.length ?? 0}
          totalSizeBytes={data?.totalSizeBytes ?? 0}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {totalKeys === 0 && (
          <Alert severity="info">
            The cache is empty. Use &quot;Warm cache&quot; to populate it.
          </Alert>
        )}

        {totalKeys > 0 && !hasResults && (
          <Alert severity="info">No tags or keys match your filter.</Alert>
        )}

        {tagGroups.map((group) => (
          <CacheTagAccordion
            key={group.tag}
            title={group.tag}
            keys={group.keys}
            entriesByKey={entriesByKey}
            disabled={isBusy}
            onPurgeKey={handlePurgeKey}
            onPurgeTag={() => handlePurgeTag(group.tag)}
          />
        ))}

        {untaggedKeys.length > 0 && (
          <CacheTagAccordion
            title="Untagged"
            keys={untaggedKeys}
            entriesByKey={entriesByKey}
            disabled={isBusy}
            onPurgeKey={handlePurgeKey}
          />
        )}
      </>
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 140px)",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "grey.200",
        background: "linear-gradient(to bottom, #ffffff, #fafafa)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <CacheManagementHeader />

        <Box sx={{ mb: 3 }}>
          <CacheActions
            isWarming={isWarming}
            isBusy={isBusy}
            onWarm={handleWarm}
            onRefresh={refreshAll}
            onPurgeAll={handlePurgeAll}
          />
        </Box>

        {warmingResult && (
          <CacheWarmingResult
            result={warmingResult}
            onDismiss={() => setWarmingResult(null)}
          />
        )}

        <Divider sx={{ mb: 3 }} />

        {renderBody()}
      </CardContent>
    </Card>
  );
};

export default CacheManagement;
